import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, InputNumber, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import * as moment from "moment";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { removeNullKeys } from "../../../utils/formatters";
import { IUser } from "../../Users";
import { IDetailedProject, IRequirement } from "..";
import ActivityTypes from "../../../enums/ActivityTypes";
import ProjectTypes from "../../../enums/ProjectTypes";
import filterOptions from "../../../utils/filterOptions";
import ITypePricingProject from "../../../interfaces/ITypePricingProject";

interface IActivityModalProps {
  visible: boolean;
  projectId: string;
  projectType?: ProjectTypes;
  handleCloseModal: (createdActivity?: IRequirement) => void;
  pricing?: string;
}

interface IActivityFormData {
  identifier: string;
  description: string;
  hours?: number;
  type: string;
  consultant_id?: string;
  project_id: string;
  allocation_month: moment.Moment;
  estimator_id: string;
  hours_factory: string;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const ActivityModal: React.FC<IActivityModalProps> = ({
  visible,
  projectId,
  projectType,
  handleCloseModal,
  pricing,
}) => {
  const { t } = useTranslation(["activities"]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [users, setUsers] = useState<IUser[]>([]);
  const [userFilter, setUserFilter] = useState<string>();
  const [hoursFactory, setHoursFactory] = useState<
    { description: string; id: string }[]
  >();
  const [project, setProject] = useState<IDetailedProject>();

  const { addToast } = useToast();
  const [form] = Form.useForm();

  const ProjectHourSeniority = useMemo(() => {
    return pricing === ITypePricingProject.HoursSeniority;
  }, [pricing]);

  useEffect(() => {
    if (visible) {
      setValidationErrors({});
    }
  }, [visible]);

  useEffect(() => {
    api
      .get(`/projects/${projectId}`)
      .then(response => {
        setProject(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description: "Occoreu um erro ao buscar o projeto, tente novamente.",
        });
      });
  }, []);

  useEffect(() => {
    if (pricing) {
      api
        .get<{ description: string; id: string }[]>("config_hours_allocation")
        .then(resp => setHoursFactory(resp.data));
    }
  }, [pricing]);

  useEffect(() => {
    setLoadingUsers(true);
    api
      .get<IUser[]>("/users", {
        params: {
          order_name: "name",
          order_direction: "ASC",
        },
      })
      .then(response => {
        setUsers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os consultores, tente novamente.",
        });
      })
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usersOptions = useMemo(() => {
    if (!users) {
      return undefined;
    }

    return users.map(manager => ({
      label: manager.name,
      value: manager.id,
    }));
  }, [users]);

  const activityTypeOptions = useMemo(() => {
    if (project?.public_project) {
      return [
        {
          label: t(`types.options.${ActivityTypes.Eventual}.label`),
          value: ActivityTypes.Eventual,
        },
      ];
    }
    return (Object.keys(ActivityTypes) as (keyof typeof ActivityTypes)[])
      .filter(key => ActivityTypes[key] !== ActivityTypes.Eventual)
      .map(key => {
        return {
          label: t(`types.options.${ActivityTypes[key]}.label`),
          value: ActivityTypes[key],
        };
      });
  }, [t]);

  const handleClose = useCallback(
    (createdActivity?: IRequirement) => {
      form.resetFields();
      handleCloseModal(createdActivity);
    },
    [form, handleCloseModal],
  );

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description = "Ocorreu um erro ao criar o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro na criação",
        description,
      });
    },
    [addToast],
  );

  const isAllocation = useMemo(() => projectType === ProjectTypes.Allocation, [
    projectType,
  ]);

  const isHoursFactory = useMemo(() => {
    return (
      pricing === ITypePricingProject.HoursSeniority &&
      projectType === ProjectTypes.Factory
    );
  }, [pricing, projectType]);

  const optionsHoursFactory = useMemo(() => {
    return hoursFactory?.map(hour => ({
      label: hour.description,
      value: hour.id,
    }));
  }, [hoursFactory]);

  const handleSubmit = useCallback(
    async (data: IActivityFormData) => {
      removeNullKeys(data);
      try {
        const schema = Yup.object().shape({
          isAllocation: Yup.boolean(),
          project_id: Yup.string().required("ID do projeto obrigatório"),
          estimator_id: Yup.string().when("isAllocation", {
            is: false,
            then: Yup.string().required("Estimador obrigatório"),
          }),
          identifier: Yup.string().when("isAllocation", {
            is: false,
            then: Yup.string().required("Identificador obrigatório"),
          }),
          description: Yup.string().when("isAllocation", {
            is: false,
            then: Yup.string().required("Descrição obrigatória"),
          }),
          allocation_month: Yup.date().when("isAllocation", {
            is: true,
            then: Yup.date().required("Mês de alocação obrigatório"),
          }),
          hours: Yup.number().nullable(),
          hours_factory: Yup.string().when("isAllocation", (_, field) => {
            return isHoursFactory
              ? field.required("Obrigatorio selecionar um perfil")
              : field;
          }),
          type: Yup.string().when("isAllocation", {
            is: false,
            then: Yup.string().required("Tipo de atividade obrigatório"),
          }),
        });

        await schema.validate(
          { ...data, isAllocation, consultant_id: userFilter },
          {
            abortEarly: false,
          },
        );

        setLoading(true);

        const {
          project_id,
          identifier,
          description,
          hours,
          type,
          allocation_month,
          estimator_id,
          hours_factory,
        } = data;

        const payload = {
          project_id,
          consultant_id: userFilter,
          identifier,
          description,
          month: allocation_month && allocation_month.month(),
          year: allocation_month && allocation_month.year(),
          hours,
          type: isAllocation ? "ALLOCATION_MONTH" : type,
          estimator_id,
          config_hour_factory_id: hours_factory,
        };

        const response = await api.post("/activities", payload);

        addToast({
          type: "success",
          title: "Atividade criada",
          description: "A atividade foi criada com sucesso",
        });

        const updateActivity = {
          ...response.data,
          hoursFactory: { configHour: { id: payload.config_hour_factory_id } },
        } as IRequirement;

        handleClose(updateActivity);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, generateErrorToast, handleClose, isAllocation, userFilter],
  );

  useEffect(() => {
    form.setFieldsValue({
      project_id: projectId,
    });
  }, [form, projectId]);

  const handleSelectUser = useCallback(user => setUserFilter(user), []);

  return (
    <Modal
      title={t("actions.options.create")}
      visible={visible}
      onOk={() => form.submit()}
      width={ProjectHourSeniority ? 800 : 600}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        initialValues={{
          project_id: projectId,
        }}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        {isAllocation ? (
          <>
            <FormItem name="project_id" hidden>
              <Input />
            </FormItem>
            <Form.Item
              name="allocation_month"
              required
              label={t("allocation_month")}
            >
              <DatePicker
                picker="month"
                style={{ width: "100%" }}
                format="MMMM YYYY"
              />
            </Form.Item>
            <FormItem
              name="consultant_id"
              required
              label={t("consultant")}
              errors={validationErrors}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                onChange={handleSelectUser}
                options={usersOptions}
                placeholder={t("consultant")}
                loading={loadingUsers}
                value={userFilter}
              />
            </FormItem>
            <FormItem label={t("hours")} errors={validationErrors}>
              <Form.Item name="hours" noStyle>
                <InputNumber />
              </Form.Item>
              <span className="ant-form-text"> h</span>
            </FormItem>
          </>
        ) : (
          <>
            <FormItem name="project_id" hidden>
              <Input />
            </FormItem>
            <FormItem
              name="type"
              required
              label={t("type")}
              errors={validationErrors}
            >
              <Select allowClear options={activityTypeOptions} />
            </FormItem>
            <FormItem
              name="identifier"
              required
              label={t("identifier")}
              errors={validationErrors}
            >
              <Input />
            </FormItem>

            <FormItem
              name="description"
              required
              label={t("description")}
              errors={validationErrors}
            >
              <Input />
            </FormItem>
            {/* <Form.Item name="consultant_id" label={t("consultant")}>
              <Select
                allowClear
                showSearch
                options={usersOptions}
                loading={loadingUsers}
              />
            </Form.Item> */}
            <FormItem
              name="consultant_id"
              label={t("consultant")}
              errors={validationErrors}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                onChange={handleSelectUser}
                options={usersOptions}
                placeholder={t("consultant")}
                loading={loadingUsers}
                value={userFilter}
              />
            </FormItem>
            {isHoursFactory ? (
              <>
                <FormItem
                  name="hours_factory"
                  label="Perfil"
                  required={isHoursFactory}
                  errors={validationErrors}
                >
                  <Select
                    allowClear
                    showSearch
                    style={{ width: "100%" }}
                    placeholder={t("selecione")}
                    filterOption={filterOptions}
                    options={optionsHoursFactory}
                  />
                </FormItem>
              </>
            ) : (
              <></>
            )}
            <FormItem
              name="estimator_id"
              required
              label={t("Estimador")}
              errors={validationErrors}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                filterOption={filterOptions}
                options={usersOptions}
                placeholder={t("Estimador")}
                loading={loadingUsers}
                value={userFilter}
              />
            </FormItem>
            <FormItem label={t("estimation")} errors={validationErrors}>
              <Form.Item name="hours" noStyle>
                <InputNumber />
              </Form.Item>
              <span className="ant-form-text"> h</span>
            </FormItem>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ActivityModal;
